import { useEffect, useMemo, useState } from 'react'
import { Navigate, useParams, useSearchParams } from 'react-router-dom'
import { NoReportSelected, ReportDetail } from '@/components/reports/ReportDetail'
import { ReportsDetailPanel, ReportsPanel } from '@/components/reports/ReportsPanel'
import { ReportTopicTree } from '@/components/reports/ReportTopicTree'
import { findCategory, findReport } from '@/lib/reportCatalog'
import { applyFilters, parseFilters, type ReportFilterSelection } from '@/lib/reportFilters'
import { applyTimeFrame, parseTimeFrame, type ReportTimeFrame } from '@/lib/reportRange'
import { useTenant } from '@/providers/TenantProvider'

/**
 * Reports — a report list beside the report.
 *
 * Two regions, not four: the catalog on the left, and on the right the report
 * with its own controls in a bar above it. The legacy screen split the filters
 * and the period into separate panes because each was a round trip; nothing
 * here is, so a reader gets the whole report and everything that shapes it in
 * one view instead of pane-hopping.
 *
 * The URL is the state. `/reports/:topic/:report` names the report, and the
 * query carries the period (`range`/`from`/`to`/`grain`) and the dimensions
 * (`f_*`), so a view is linkable, survives a refresh, and keeps its period as
 * the reader moves between reports — the tree links append the current query
 * rather than dropping it. The route is registered once with optional params,
 * so the `reports.view` guard is not restated per sub-route.
 *
 * Every control applies on change. `replace` keeps a dozen experiments with the
 * period out of the back button.
 *
 * Tenant scope: the shell fetches nothing yet, and the report is keyed on the
 * selected tenant so no state survives a switch. The report hooks the child
 * tickets add must put `selectedTenantId` in their query keys rather than lean
 * on `Layout`'s remount — see `useEquipment`.
 */
export function ReportsPage() {
  const { topic, report: reportSlug } = useParams<{ topic?: string; report?: string }>()
  const [params, setParams] = useSearchParams()
  const { selectedTenantId } = useTenant()

  const selected = findReport(topic, reportSlug)
  const category = findCategory(topic)
  const report = selected?.report ?? null

  // Re-derived on every render so a rolling preset stays anchored to today even
  // if the tab has been open across midnight.
  const frame = useMemo(() => parseTimeFrame(params, new Date()), [params])
  const filters = useMemo(() => parseFilters(params, report), [params, report])

  const [treeCollapsed, setTreeCollapsed] = useState(false)
  const [openTopics, setOpenTopics] = useState<Set<string>>(
    () => new Set(category ? [category.slug] : []),
  )

  useEffect(() => {
    if (!category) return
    setOpenTopics((open) => (open.has(category.slug) ? open : new Set(open).add(category.slug)))
  }, [category])

  function toggleTopic(slug: string) {
    setOpenTopics((open) => {
      const next = new Set(open)
      if (!next.delete(slug)) next.add(slug)
      return next
    })
  }

  function setFrame(next: ReportTimeFrame) {
    setParams(applyTimeFrame(params, next), { replace: true })
  }

  function setFilters(next: ReportFilterSelection) {
    setParams(applyFilters(params, next), { replace: true })
  }

  // A URL naming a report that does not exist is a typo or a stale bookmark;
  // land on the page rather than an empty pane, keeping the period.
  if ((topic && !category) || (reportSlug && !selected)) {
    return <Navigate to={{ pathname: '/reports', search: params.toString() }} replace />
  }

  return (
    <div className='flex h-full min-h-0 gap-3 bg-muted/20 p-3'>
      <ReportsPanel
        title='Reports'
        width='w-64'
        collapsed={treeCollapsed}
        onToggle={() => setTreeCollapsed((on) => !on)}
      >
        <ReportTopicTree
          openTopics={openTopics}
          activeReportId={report?.id ?? null}
          search={params.toString() ? `?${params.toString()}` : ''}
          onToggleTopic={toggleTopic}
        />
      </ReportsPanel>

      <ReportsDetailPanel>
        <div className='h-full' key={selectedTenantId ?? 'no-tenant'}>
          {selected ? (
            <ReportDetail
              report={selected.report}
              categoryName={selected.category.name}
              frame={frame}
              filters={filters}
              onFrame={setFrame}
              onFilters={setFilters}
            />
          ) : (
            <NoReportSelected />
          )}
        </div>
      </ReportsDetailPanel>
    </div>
  )
}
